import type { LineItem } from "@graphql/generated/types";
import { useCart } from "@hooks/useCart";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useApi } from "@lib/helpers/useApi";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type { ProductVariant } from "@lib/product/types";
import { cartItemsOnStock } from "@lib/utils/cartProductUtility";
import { useStore } from "@store/index";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";

type UseCartItemsAvailabilityParams = {
    cartId?: string;
    lineItems?: LineItem[];
    /** Used instead of `lineItems` when checking SKUs that are not in the cart (e.g. PDP variants). */
    skus?: string[];
    enabled?: boolean;
};

type UseCartItemsAvailabilityResult = {
    hasOutOfStockItems: boolean;
    outOfStockSkus: string[];
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    isFetching: boolean;
    fetchStatus: "fetching" | "paused" | "idle";
    checkItemsOnStock: (cartId: string, lineItems: LineItem[]) => Promise<boolean>;
};

const NO_SKUS: string[] = [];

const getSkus = (lineItems?: LineItem[]): string[] =>
    (lineItems ?? []).map((item) => item?.variant?.sku).filter(Boolean);

// Quantities are part of the key so availability is re-checked after quantity changes.
const getLineItemsKey = (lineItems?: LineItem[]): string[] =>
    (lineItems ?? [])
        .filter((item) => item?.variant?.sku)
        .map((item) => `${item.variant.sku}:${item.quantity}`);

const availabilityQueryKey = (cartId: string | undefined, items: string[]): QueryKey => [
    QueryKeys.getProductsAvailability,
    { cartId, items },
];

export const useCartItemsAvailability = ({
    cartId,
    lineItems,
    skus: plainSkus,
    enabled = true,
}: UseCartItemsAvailabilityParams = {}): UseCartItemsAvailabilityResult => {
    const queryClient = useQueryClient();
    const skus = lineItems ? getSkus(lineItems) : (plainSkus ?? NO_SKUS);
    const itemsKey = lineItems ? getLineItemsKey(lineItems) : skus;

    const {
        data: stock,
        isLoading,
        isError,
        isSuccess,
        isFetching,
        fetchStatus,
    } = useApi.useAsync(
        availabilityQueryKey(cartId, itemsKey),
        () => cartItemsOnStock(cartId, skus),
        {
            enabled: enabled && Boolean(cartId) && skus.length > 0,
            staleTime: 0,
        }
    );

    const checkItemsOnStock = async (checkCartId: string, checkLineItems: LineItem[]) => {
        const result = await queryClient.fetchQuery({
            queryKey: availabilityQueryKey(checkCartId, getLineItemsKey(checkLineItems)),
            queryFn: () => cartItemsOnStock(checkCartId, getSkus(checkLineItems)),
            staleTime: 0,
        });
        return result.onStock;
    };

    return {
        hasOutOfStockItems: stock?.onStock === false,
        outOfStockSkus: stock?.outOfStockSkus ?? NO_SKUS,
        isLoading,
        isError,
        isSuccess,
        isFetching,
        fetchStatus,
        checkItemsOnStock,
    };
};

/** PDP variant availability: shares one request per product; a failed check leaves sizes enabled. */
export const useVariantsAvailability = (
    variants?: ProductVariant[]
): Pick<UseCartItemsAvailabilityResult, "outOfStockSkus" | "isLoading"> & {
    isChecked: boolean;
    /** Cart creation failed, so the check cannot run; call `retryCart` to try again. */
    isCartError: boolean;
    retryCart: () => void;
} => {
    const cartId = useStore((state) => state.data.me?.cartInfo?.cart?.id);
    const hasFreshData = useStore((state) => state.data.hasFreshData);
    const { ensureCart } = useCart();

    // The availability API needs a cartId; wait for the active cart fetch so we don't create a duplicate.
    const {
        isError: isEnsureCartError,
        isFetching: isEnsuringCart,
        refetch: retryEnsureCart,
    } = useApi.useAsync(
        [QueryKeys.ensureCart],
        async () => {
            try {
                const cart = await ensureCart();
                if (!cart?.id) throw new Error("Cart creation returned no cart");
                return cart.id;
            } catch (error) {
                Logger.error(ServiceType.WEB, "Failed to create cart for PDP availability check", {
                    error,
                    tag: LogTag.PRODUCT,
                });
                throw error;
            }
        },
        { enabled: hasFreshData && !cartId, retry: 1, staleTime: 0 }
    );
    const isCartError = !cartId && isEnsureCartError && !isEnsuringCart;
    // Sizes CommerceTools reports as out of stock are already disabled, so only check the rest.
    const variantSkus = (variants ?? []).map((variant) => variant.sku).filter(Boolean);
    const skus = (variants ?? [])
        .filter((variant) =>
            variant?.availability?.some(
                (channel) => channel?.key === process.env.NEXT_PUBLIC_STORE_KEY && channel.isOnStock
            )
        )
        .map((variant) => variant.sku)
        .filter(Boolean);
    const allVariantsSoldOut = variantSkus.length > 0 && skus.length === 0;
    const { outOfStockSkus, isLoading, isError, isSuccess, isFetching, fetchStatus } =
        useCartItemsAvailability({ cartId, skus });
    return {
        outOfStockSkus: allVariantsSoldOut ? variantSkus : outOfStockSkus,
        isLoading,
        isChecked:
            allVariantsSoldOut ||
            (Boolean(cartId) &&
                isSuccess &&
                !isLoading &&
                !isError &&
                !isFetching &&
                fetchStatus === "idle"),
        isCartError,
        retryCart: () => void retryEnsureCart(),
    };
};
